/**
 * What a vehicle sounds like (owner: "different cars should have different sounding engines").
 * Content may set `sound: { engine, pitch, gears }` on a vehicle; everything else gets a voice from its class, tags and
 * mass, so the next hundred vehicles need no field. The synth itself lives in src/engine/audio.ts (`Audio.engine`).
 */
import type { Vehicle } from '../content/types';

export type EngineKind = 'inline4' | 'v6' | 'v8' | 'diesel' | 'electric' | 'bike' | 'twostroke' | 'outboard' | 'prop' | 'turbine' | 'rotor' | 'rail';
export interface EngineVoice { kind: EngineKind; /** frequency multiplier (small engines high, big ones low) */ pitch: number; /** gears in the rpm model (1 = a continuous whine) */ gears: number }

const GEARS: Record<EngineKind, number> = { inline4: 4, v6: 5, v8: 4, diesel: 6, electric: 1, bike: 5, twostroke: 3, outboard: 1, prop: 1, turbine: 1, rotor: 1, rail: 1 };

/** The default voice for a vehicle definition, overridden by its `sound` field. */
export function engineVoiceOf(def: Vehicle): EngineVoice {
  const t = new Set(def.tags), m = def.stats.mass;
  let kind: EngineKind;
  if (def.class === 'helicopter') kind = 'rotor';
  else if (def.class === 'plane') kind = t.has('military') || m > 4000 ? 'turbine' : 'prop';
  else if (def.class === 'train') kind = 'rail';
  else if (def.class === 'boat') kind = m < 600 ? 'twostroke' : 'outboard';
  else if (def.class === 'bike') kind = m < 120 ? 'twostroke' : 'bike';
  else if (def.power === 'electric' || t.has('electric')) kind = 'electric';
  else if (def.class === 'truck' || def.class === 'bus' || def.class === 'tank' || m > 3500) kind = 'diesel';
  else if (def.class === 'sports' || t.has('muscle') || t.has('drift') || t.has('luxury')) kind = def.stats.maxSpeed > 55 || t.has('muscle') ? 'v8' : 'v6';
  else kind = m > 1900 ? 'v6' : 'inline4';
  // heavier → lower: a compact sits above 1, a lorry well below
  const ref: Record<EngineKind, number> = { inline4: 1300, v6: 1700, v8: 1900, diesel: 6000, electric: 1600, bike: 200, twostroke: 120, outboard: 900, prop: 900, turbine: 6000, rotor: 1500, rail: 60000 };
  const pitch = Math.max(0.55, Math.min(1.8, Math.pow(ref[kind] / Math.max(50, m), 0.25)));
  const s = def.sound;
  return { kind: s?.engine ?? kind, pitch: s?.pitch ?? Math.round(pitch * 100) / 100, gears: s?.gears ?? GEARS[s?.engine ?? kind] };
}
